/**
 * Strategy Mode Banner — surfaces the active contest field-size posture so the
 * user always knows whether the ILP is currently building for large-field
 * (contrarian / Milly Maker) or small-field (chalk-eating) play.
 *
 * Derived purely from the active preset in `presets` map + `active` key.
 */
import React from "react";
import { Shield, Crosshair, Flame } from "@phosphor-icons/react";

function modeFromFieldSize(fieldSize) {
  if (!fieldSize) return "unknown";
  if (fieldSize < 5000) return "small";
  if (fieldSize <= 50000) return "mid";
  return "large";
}

const MODE_CONFIG = {
  small: {
    label: "SMALL-FIELD MODE",
    color: "#39FF14",
    Icon: Shield,
    tagline: "Chalk-eating strategy. Anti-chalk pivots RELAXED — you only need to outscore a small fraction of the field.",
  },
  mid: {
    label: "MID-FIELD MODE",
    color: "#FFCC00",
    Icon: Crosshair,
    tagline: "Balanced attack. Blend high-floor favorites with 1–2 targeted contrarian pivots.",
  },
  large: {
    label: "LARGE-FIELD MODE",
    color: "#FF3B30",
    Icon: Flame,
    tagline: "Milly Maker attack. Anti-chalk governor ENGAGED — sub-5% leverage pivots required per lineup.",
  },
  unknown: {
    label: "STRATEGY MODE",
    color: "#71717a",
    Icon: Crosshair,
    tagline: "Select a Contest Target to activate a field-size-aware posture.",
  },
};

const CONTEST_LABEL = {
  MILLY: "Milly Maker",
  GPP: "GPP",
  CASH: "Cash",
  SHOWDOWN: "Showdown",
};

/**
 * `contestType` + `fieldSize` (live values from the Smart Optimizer's contest
 * picker) take precedence over the preset map. Without them the banner kept
 * echoing the last-saved preset — a user on the GPP tab with a 176k field
 * still read "Milly Maker · 200,000 entries".
 */
export default function StrategyModeBanner({ presets, activeKey, contestType, fieldSize }) {
  const presetMeta = presets?.[activeKey];
  const liveField = Number(fieldSize) > 0 ? Number(fieldSize) : null;
  const meta = contestType
    ? { label: CONTEST_LABEL[contestType] || contestType, field_size: liveField }
    : presetMeta;
  const mode = modeFromFieldSize(meta?.field_size);
  const cfg = { ...MODE_CONFIG[mode] };
  if (mode === "large" && contestType && contestType !== "MILLY") {
    cfg.tagline = "Large-field attack. Anti-chalk governor ENGAGED — sub-5% leverage pivots required per lineup.";
  }
  const Icon = cfg.Icon;
  return (
    <div
      data-testid="strategy-mode-banner"
      data-strategy-mode={mode}
      className="border-l-4 bg-zinc-950 border-zinc-800 p-3 flex items-start gap-3"
      style={{ borderLeftColor: cfg.color }}
    >
      <Icon size={20} weight="fill" style={{ color: cfg.color }} className="mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            data-testid="strategy-mode-label"
            className="text-[10px] mono tracking-widest font-bold"
            style={{ color: cfg.color }}
          >
            {cfg.label}
          </span>
          {meta && (
            <span className="text-[10px] mono text-zinc-500">
              · {meta.label} · {meta.field_size?.toLocaleString()} entries
            </span>
          )}
        </div>
        <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{cfg.tagline}</p>
      </div>
    </div>
  );
}
